import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import OrgTag from '../../components/OrgTag';
import PlacementForm from '../../components/PlacementForm';
import { OrgBadge, teamName } from '../../components/PlacementsTable';
import { fmtDate, fmtNumber, useFetch, usePagedSimple } from '../../hooks';
import { Purpose, Alert, Badge, Button, Card, Skeleton, Stat } from '../../ui/basics';
import { DataTable } from '../../ui/DataTable';
import { Drawer } from '../../ui/overlay';

const TONE = { error: 'danger', warning: 'warning', info: '' };
const SECTIONS = [['error', 'Errors', 'These make numbers wrong.'], ['warning', 'Warnings', 'These weaken or confuse the numbers.'], ['info', 'Tidy-ups', 'Inconsistencies that split reports.']];

function Detail({ check, org, lead, onClose, onChanged }) {
  const { user, hasPage } = useAuth();
  const [fixing, setFixing] = useState(null);
  const list = usePagedSimple(`analytics/data-health/${check.id}/`, { organization: org, lead }, 10);
  const canFix = user.can_edit && hasPage('placements');
  const master = check.kind === 'master';

  const columns = master ? [
    { key: 'name', label: 'Name', render: (r) => <b>{r.name}</b> },
    { key: 'organization', label: 'Organization', render: (r) => <OrgTag orgKey={r.organization} /> },
    { key: 'copies', label: 'Copies', align: 'right' },
    { key: 'ids', label: 'Record IDs', render: (r) => r.ids.join(', ') },
  ] : [
    { key: 'candidate_name', label: 'Candidate', render: (r) => <b>{r.candidate_name}</b> },
    ...(org === 'combined' ? [{ key: 'organization', label: 'Organization', render: (r) => <OrgBadge name={r.organization} /> }] : []),
    { key: 'client', label: 'Client' }, { key: 'recruiter', label: 'Recruiter' },
    { key: 'lead', label: 'Team', render: (r) => teamName(r.lead) },
    { key: 'date_of_joining', label: 'Start', render: (r) => fmtDate(r.date_of_joining) },
    { key: 'project_end', label: 'End', render: (r) => fmtDate(r.project_end) },
    { key: 'detail', label: 'What is wrong', render: (r) => <span style={{ color: 'var(--danger)' }}>{r.detail}</span> },
    ...(canFix ? [{ key: 'fix', label: '', align: 'right', render: (r) => <Button size="sm" onClick={() => setFixing(r)}>Fix</Button> }] : []),
  ];
  const { detail, ...fixable } = fixing || {};

  return (
    <Drawer wide title={check.title} onClose={onClose}>
      <div className="stack">
        <p className="muted">{check.description}</p>
        {master && <Alert tone="info">Merge or remove duplicates in <Link to="/directory" onClick={onClose}>Clients &amp; Recruiters</Link>. A record that placements still use cannot be deleted; archive it instead.</Alert>}
        <div className="card flush"><DataTable columns={columns} paged={list} rowKey={(r) => r.s_no || r.ids.join('-')} noun={master ? 'groups' : 'placements'} /></div>
      </div>
      {fixing && <PlacementForm placement={fixable} defaultOrg={org} onClose={() => setFixing(null)} onSaved={() => { setFixing(null); list.reload(); onChanged(); }} />}
    </Drawer>
  );
}

export default function DataHealth({ org, lead }) {
  const { data, loading, error, reload } = useFetch('analytics/data-health/', { organization: org, lead });
  const [open, setOpen] = useState(null);
  const needAttention = data ? data.checks.filter((c) => c.count > 0 && c.severity !== 'info').length : 0;

  return (
    <div className="stack">
      <Purpose what="Lists problems in the placement data and master lists (wrong dates, missing rates, unknown names, spelling variants, duplicates) that make the other analytics inaccurate." use="Start with Errors, open a row list, and use Fix to correct the placement. Then work through Warnings and Tidy-ups." care="Fixing data here improves every number on the other tabs." />
      <Card title="Data health" subtitle="Problems in the placement data and master lists that distort the numbers. Each count opens the exact rows.">
        {error && <Alert>{error}</Alert>}
        {!data ? <Skeleton h={70} /> : (
          <div className="grid cols-3">
            <Stat label="Placements without errors or warnings" value={`${data.clean_percent}%`} sub={`${fmtNumber(data.total - data.rows_with_problems)} of ${fmtNumber(data.total)} placements`} />
            <Stat label="Placements to look at" value={fmtNumber(data.rows_with_problems)} sub="have at least one error or warning" />
            <Stat label="Checks needing attention" value={needAttention} sub={`of ${data.checks.filter((c) => c.severity !== 'info').length} error and warning checks`} />
          </div>
        )}
      </Card>
      {loading && !data && <Skeleton h={200} />}
      {data && SECTIONS.map(([severity, title, hint]) => (
        <Card key={severity} flush title={title} subtitle={hint}>
          <div className="table-wrap"><table className="table" style={{ minWidth: 0 }}>
            <tbody>
              {data.checks.filter((c) => c.severity === severity).map((c) => (
                <tr key={c.id} className={c.count ? 'clickable' : ''} onClick={c.count ? () => setOpen(c) : undefined}>
                  <td style={{ width: '60%' }}><b>{c.title}</b><div className="muted">{c.description}</div></td>
                  <td className="r">{c.distinct !== null && c.count > 0 && <span className="muted">{c.distinct} different {c.id.endsWith('_variants') ? 'spellings' : 'names'}</span>}</td>
                  <td className="r" style={{ width: 130 }}>{c.count ? <Badge tone={TONE[c.severity]}>{fmtNumber(c.count)} {c.kind === 'master' ? 'groups' : 'placements'}</Badge> : <Badge tone="success">All good</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </Card>
      ))}
      {open && <Detail check={open} org={org} lead={lead} onClose={() => setOpen(null)} onChanged={reload} />}
    </div>
  );
}
